"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { usePrivy } from "@privy-io/react-auth";
import { signIn, useSession } from "next-auth/react";
import { usePrivyAvailable } from "@/components/WalletConnectProvider";
import { clientLogger } from "@/lib/client-logger";

/**
 * Prompt shown to a signed-in user who has no email on file — i.e. they came
 * in through a wallet-only Privy login. Adding an email is how they become
 * eligible for prize drawings (the winner is contacted by email) and gives
 * them a way to be reached.
 *
 * Uses Privy's linkEmail, which collects AND verifies the address via OTP in
 * one step. Once Privy reports the linked email, we re-run the Privy→NextAuth
 * exchange so the server picks it up (findOrCreatePrivyUser/maybeRefreshEmail
 * set emailVerified=true) and the session refreshes — at which point this
 * prompt stops rendering.
 *
 * `variant` controls presentation: "banner" is a full-width strip for the
 * dashboard; "inline" is a compact row for the profile settings field.
 */
export function AddEmailPrompt({
  variant = "banner",
}: {
  variant?: "banner" | "inline";
}) {
  const privyAvailable = usePrivyAvailable();
  // A user with no email almost always authenticated via a wallet, so Privy is
  // available. If it somehow isn't, render nothing rather than a dead button.
  if (!privyAvailable) return null;
  return <AddEmailPromptInner variant={variant} />;
}

function AddEmailPromptInner({ variant }: { variant: "banner" | "inline" }) {
  const { user, linkEmail, getAccessToken } = usePrivy();
  const { update } = useSession();
  const router = useRouter();
  const [syncing, setSyncing] = useState(false);
  // Guard so the sync effect fires once per linked email, not on every render.
  const synced = useRef(false);

  const privyEmail = user?.email?.address ?? null;

  useEffect(() => {
    if (!privyEmail || synced.current) return;
    synced.current = true;
    setSyncing(true);
    (async () => {
      try {
        const token = await getAccessToken();
        if (token) {
          await signIn("privy", { token, redirect: false });
          await update();
          router.refresh();
        }
      } catch (err) {
        clientLogger.warn("Failed to sync linked email into session", {
          error: err instanceof Error ? err.message : "Unknown",
        });
        synced.current = false;
      } finally {
        setSyncing(false);
      }
    })();
  }, [privyEmail, getAccessToken, update, router]);

  if (variant === "inline") {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-yellow-700">
        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>No email on file</span>
        <button
          onClick={() => linkEmail()}
          disabled={syncing}
          className="font-medium underline hover:text-yellow-900 disabled:opacity-50"
        >
          {syncing ? "Saving…" : "Add & verify email"}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-md px-4 py-3 mb-6 flex items-center justify-between gap-4">
      <p className="text-sm text-yellow-800">
        Add an email address to finish setting up your account. A verified email
        is required to be entered into prize drawings.
      </p>
      <button
        onClick={() => linkEmail()}
        disabled={syncing}
        className="text-sm font-medium text-yellow-800 underline hover:text-yellow-900 disabled:opacity-50 whitespace-nowrap"
      >
        {syncing ? "Saving…" : "Add email"}
      </button>
    </div>
  );
}
